/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {
  CurrentConditionsObject,
  LocationPointObject,
  PrecipitationForecastObject,
  WeatherForecastObject,
  ThreeDayForecastObject,
} from 'jotai/weather';

interface PrecipitationForecastResponse {
  forecasts: PrecipitationForecastObject[];
}

interface LocationPointReponse {
  location: LocationPointObject;
}

interface FifteenMinuteForecastResponse {
  dayOfWeek: string[];
  iconCode: number[];
  iconCodeExtend: number[];
  precipChance: number[];
  precipRate: number[];
  precipType: string[];
  relativeHumidity: number[];
  snowRate: number[];
  temperature: number[];
  temperatureFeelsLike: number[];
  validTimeLocal: string[];
  windDirection: number[];
  windDirectionCardinal: string[];
  windSpeed: number[];
  wxPhraseLong: string[];
  wxPhraseShort: string[];
  wxSeverity: number[];
}

interface ThreeDayForecastReponse {
  calendarDayTemperatureMax: number[];
  calendarDayTemperatureMin: number[];
  daypart: { cloudCover: number[] }[];
}

interface TileServerSeriesResponse {
  seriesInfo: {
    radar: {
      series: { ts: number }[];
    };
  };
}

export class WeatherService {
  private key: string;

  constructor() {
    this.key = process.env.REACT_APP_WEATHERKEY || '';
  }

  async getFifteenMinuteForecast(latitude: number, longitude: number): Promise<WeatherForecastObject[]> {
    let urlLatLong = `https://api.weather.com/v3/wx/forecast/fifteenminute?geocode=${latitude},${longitude}&units=e&language=en-US&format=json&apiKey=${this.key}`;
    const response = await fetch(urlLatLong);
    const forecastResponse = (await response.json()) as FifteenMinuteForecastResponse;

    const forecasts: WeatherForecastObject[] = [];

    forecastResponse.validTimeLocal.forEach((_validTimeLocal, index) => {
      const forecast = Object.entries(forecastResponse).reduce((previousValue, currentValue) => {
        return { ...previousValue, [currentValue[0]]: currentValue[1][index] };
      }, {});
      forecasts.push(forecast as WeatherForecastObject);
    });
    return forecasts;
  }

  async getThreeDayForecast(latitude: number, longitude: number): Promise<ThreeDayForecastObject> {
    let urlLatLong = `https://api.weather.com/v3/wx/forecast/daily/3day?geocode=${latitude},${longitude}&units=e&language=en-US&format=json&apiKey=${this.key}`;
    const response = await fetch(urlLatLong);
    const forecastResponse = (await response.json()) as ThreeDayForecastReponse;
    const forecast: ThreeDayForecastObject = {
      calendarDayTemperatureMax: forecastResponse.calendarDayTemperatureMax[0],
      calendarDayTemperatureMin: forecastResponse.calendarDayTemperatureMin[0],
      cloudCover: forecastResponse.daypart[0].cloudCover[1],
    };
    return forecast;
  }

  async getCurrentConditions(latitude: number, longitude: number): Promise<CurrentConditionsObject> {
    let urlLatLong = `https://api.weather.com/v3/wx/observations/current?geocode=${latitude},${longitude}&units=e&language=en-US&format=json&apiKey=${this.key}`;
    const response = await fetch(urlLatLong);
    return (await response.json()) as CurrentConditionsObject;
  }

  async getPrecipitationForecast(latitude: number, longitude: number): Promise<PrecipitationForecastObject> {
    let urlLatLong = `https://api.weather.com/v1/geocode/${latitude}/${longitude}/forecast/precipitation.json?language=en-US&units=e&apiKey=${this.key}`;
    const response = await fetch(urlLatLong);
    const forecast = (await response.json()) as PrecipitationForecastResponse;
    return forecast.forecasts[0];
  }

  async getLocationPoint(latitude: number, longitude: number): Promise<LocationPointObject> {
    let urlLatLong = `https://api.weather.com/v3/location/point?geocode=${latitude},${longitude}&language=en-US&format=json&apiKey=${this.key}`;
    const response = await fetch(urlLatLong);
    const locationPoint = (await response.json()) as LocationPointReponse;
    return locationPoint.location;
  }

  async getRadarTilePaths(): Promise<string[]> {
    const url = `https://api.weather.com/v3/TileServer/series/productSet/PPAcore?apiKey=${this.key}`;
    const response = await fetch(url);
    const tileServerSeries = (await response.json()) as TileServerSeriesResponse;
    return tileServerSeries.seriesInfo.radar.series.map(
      (tilesSeries) =>
        `https://api.weather.com/v3/TileServer/tile/radar?ts=${tilesSeries.ts}&xyz={x}:{y}:{z}&apiKey=${this.key}`
    );
  }
}

export const weatherService = new WeatherService();
